import {
  ApacCandidateHeader,
  ApacCandidateFooter,
  ApacCandidateHeaderFooterLinkProvider,
} from '@seek/apac-candidate-header-footer';
import { useMelwaysInfo, useMelwaysLink } from '@seek/melways-react';
import {
  type CountrySiteName,
  getZoneFromSite,
  type Country,
  type Language,
} from '@seek/melways-sites';
import { useTranslations } from '@vocab/react';
import { Page, Box, Divider, Stack } from 'braid-design-system';
import { Helmet } from 'react-helmet';
import { Route, Routes, useLocation } from 'react-router-dom';

import { MyActivityLayout } from 'src/components/MyActivityLayout/MyActivityLayout';
import { SkipToContent } from 'src/components/ScreenReader/SkipToContent';
import { Site } from 'src/config';
import { APPLIED_ROUTE, SAVED_ROUTE, JOB_TYPE } from 'src/const/app';
import JobsContainer from 'src/containers/JobsContainer';

import { analyticsClient } from '../../modules/analytics/analytics';

import translations from './App.vocab';
import { useUser } from './hooks';

const App = () => {
  const { t } = useTranslations(translations);
  const location = useLocation();
  const { user, authenticationStatus } = useUser();
  const { personalDetails, emailAddress } = user || {};
  const { brand, country, language, site } = useMelwaysInfo();

  analyticsClient.setState({
    country: country?.toUpperCase() as Country,
    siteCountry: country,
    siteLanguage: language as Language,
    zone: getZoneFromSite(site as CountrySiteName),
    brand,
  });

  const currentUrl = `${location.pathname}${location.search}`;
  const urlWithoutLang = currentUrl.replace(/^\/(en|th|id)/, '');
  const isLanguageSwitcher =
    site === Site.jobstreetID || site === Site.jobsdbTH;
  const languageSwitcher = isLanguageSwitcher
    ? {
        en: {
          url: `${urlWithoutLang}`,
        },
        [country!]: {
          url: `/${country}${urlWithoutLang}`,
        },
      }
    : undefined;
  const customLinkComponent = {
    sendLinkEvent: () => {},
  };

  return (
    <>
      <Helmet>
        <title>{t('Page title')}</title>
      </Helmet>
      <SkipToContent>{t('Skip to content')}</SkipToContent>
      <ApacCandidateHeaderFooterLinkProvider value={customLinkComponent}>
        <Page
          footerPosition="belowFold"
          footer={
            <>
              <Box position="relative" zIndex={1}>
                <Divider />
              </Box>
              <ApacCandidateFooter
                isExpandedVersion={true}
                authenticationStatus={authenticationStatus}
                widthMode="large"
              />
            </>
          }
        >
          <Stack space="xlarge">
            <ApacCandidateHeader
              leanHeader={false}
              authenticationStatus={authenticationStatus}
              userProfile={{
                firstName: personalDetails?.firstName || '',
                lastName: personalDetails?.lastName || '',
                email: emailAddress,
              }}
              activeGlobalTab="jobs"
              languageSwitcher={languageSwitcher}
              onLanguageSwitch={() => {}}
              widthMode="large"
              divider={true}
            />
            <Routes>
              <Route path="/" element={<MyActivityLayout />}>
                <Route
                  path={useMelwaysLink(SAVED_ROUTE)}
                  element={
                    <JobsContainer
                      authenticationStatus={authenticationStatus}
                      type={JOB_TYPE.saved}
                    />
                  }
                />
                <Route
                  path={useMelwaysLink(APPLIED_ROUTE)}
                  element={
                    <JobsContainer
                      authenticationStatus={authenticationStatus}
                      type={JOB_TYPE.applied}
                    />
                  }
                />
              </Route>
            </Routes>
          </Stack>
        </Page>
      </ApacCandidateHeaderFooterLinkProvider>
    </>
  );
};

export default App;
